import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from './client.ts';
import { useSession } from '../auth/session.tsx';
import { queryDefaults } from './queries.ts';
import { fileName } from '../documentName.ts';

/**
 * A client's own documents (142): its folders, the files uploaded into them, and whether the reader may upload.
 * Nothing opens until Defender's scan says clean; while anything is still being checked the list asks again every
 * few seconds, which is also when the API reads Defender's answer.
 */
export interface ClientFolder { id: string; name: string; standard: string | null; parent_id: string | null; depth: number }
export interface ClientUpload {
  id: string;
  folder_id: string;
  filename: string;
  content_type: string | null;
  bytes: number;
  status: 'awaiting_scan' | 'clean' | 'malicious' | 'scan_failed' | 'scan_unanswered';
  scan_result: string | null;
  uploaded_at: string;
  uploaded_by: string | null;
  openable: boolean;
}
/** A top folder from the catalogue (145) that this client lacks and the reader may add. */
export interface AddableFolder { code: string; name: string }
export interface ClientFolders {
  folders: ClientFolder[]; uploads: ClientUpload[]; may_upload: boolean; limit_bytes: number; max_depth: number;
  addable: AddableFolder[];
}

const key = (clientId: string) => ['client', clientId, 'folders'] as const;

export function useClientFolders(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: key(clientId),
    queryFn: () => apiFetch<ClientFolders>(`/clients/${clientId}/folders`, getToken),
    ...queryDefaults,
    refetchInterval: (q) => (q.state.data?.uploads.some((u) => u.status === 'awaiting_scan') ? 4000 : false),
  });
}

export function useCreateFolder(clientId: string) {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, parentId }: { name: string; parentId: string }) =>
      apiFetch<{ id: string }>(`/clients/${clientId}/folders`, getToken, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name, parent_id: parentId }),
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: key(clientId) }); },
  });
}

/** Adds one of the catalogue's top folders by its code (145); the database decides whether the reader may. */
export function useAddTopFolder(clientId: string) {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ id: string }>(`/clients/${clientId}/folders/top`, getToken, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }),
      }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: key(clientId) }); },
  });
}

/** One file, streamed as it is: its name in a header (never the URL), its bytes as the body. */
export async function uploadOne(getToken: () => Promise<string>, clientId: string, folderId: string, file: File): Promise<{ id: string }> {
  const res = await fetch(`/api/clients/${clientId}/folders/${folderId}/files`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${await getToken()}`,
      'content-type': 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      ...(file.type ? { 'x-file-type': file.type } : {}),
    },
    body: file,
  });
  if (!res.ok) {
    let message = `The file could not be uploaded (${res.status}).`;
    let hint: string | undefined;
    try { const b = (await res.json()) as { error?: string; hint?: string }; message = b.error ?? message; hint = b.hint; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, hint);
  }
  return (await res.json()) as { id: string };
}

/** The folder at the end of a path under a folder, made where missing (143): names in the body, never the URL. */
export function ensurePath(getToken: () => Promise<string>, clientId: string, parentId: string, path: string[]): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/clients/${clientId}/folders/ensure`, getToken, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ parent_id: parentId, path }),
  });
}

/** The client's Real Estate folder, found or made: only where its Clio copies have Real Estate (the server checks). */
export function realEstateFolder(getToken: () => Promise<string>, clientId: string): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(`/clients/${clientId}/folders/real-estate`, getToken, { method: 'POST' });
}

export function useRefreshFolders(clientId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: key(clientId) });
}

/** One clean upload's bytes, through Procura (audited), with the server's name for it. */
export async function fetchUpload(getToken: () => Promise<string>, clientId: string, uploadId: string): Promise<{ blob: Blob; name: string }> {
  const res = await fetch(`/api/clients/${clientId}/uploads/${uploadId}/file`, { headers: { authorization: `Bearer ${await getToken()}` } });
  if (!res.ok) {
    let message = `The document could not be opened (${res.status}).`;
    let hint: string | undefined;
    try { const b = (await res.json()) as { error?: string; hint?: string }; message = b.error ?? message; hint = b.hint; } catch { /* not JSON */ }
    throw new ApiError(res.status, message, hint);
  }
  return { blob: await res.blob(), name: fileName(res.headers.get('content-disposition')) };
}

/** Saves one clean upload under "Last, First - <file name>". The only way an upload leaves the page. */
export async function downloadUpload(getToken: () => Promise<string>, clientId: string, uploadId: string): Promise<void> {
  const { blob, name } = await fetchUpload(getToken, clientId, uploadId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
